import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Sparkles, Loader2, Copy, Send, MessageCircle } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:3001';

const STATUS_LABEL = {
  em_selecao: { l:'Em Seleção',  bg:'bg-blue-100 text-blue-700',    dot:'bg-blue-500' },
  aprovado:   { l:'Aprovado ✅', bg:'bg-green-100 text-green-700',  dot:'bg-green-500' },
  recusado:   { l:'Recusado ❌', bg:'bg-red-100 text-red-600',      dot:'bg-red-400' },
  entrevista: { l:'Entrevista 📅',bg:'bg-yellow-100 text-yellow-700',dot:'bg-yellow-500' },
};

// ── IA Mikhal: gera e envia mensagem de seleção ───────────────
function MikhalMensagem({ candidato, onEnviar }) {
  const [aberto,    setAberto]    = useState(false);
  const [gerando,   setGerando]   = useState(false);
  const [mensagem,  setMensagem]  = useState('');
  const [enviando,  setEnviando]  = useState(false);
  const [tipo,      setTipo]      = useState('selecao');

  const gerar = async () => {
    setGerando(true);
    try {
      const { data } = await api.post('/selecao/ia-mensagem', {
        candidato_nome: candidato.nome,
        vaga_titulo:    candidato.vaga_titulo || 'vaga disponível',
        tipo,
      });
      setMensagem(data.mensagem);
    } catch {
      toast.error('Erro ao gerar mensagem.');
    }
    setGerando(false);
  };

  const enviarEAbrir = async () => {
    if (!mensagem.trim()) return;
    setEnviando(true);
    try {
      const { data: conv } = await api.post('/chat/conversas', { participante_id: candidato.user_id });
      await api.post(`/chat/conversas/${conv.id}/mensagens`, { conteudo: mensagem });
      toast.success('💬 Mensagem enviada pelo chat!');
      setMensagem('');
      setAberto(false);
      onEnviar(candidato); // navega para chat
    } catch {
      toast.error('Erro ao enviar mensagem.');
    }
    setEnviando(false);
  };

  return (
    <div className="border border-purple-200 bg-purple-50/50 rounded-2xl overflow-hidden">
      <button onClick={() => { setAberto(a => !a); if (!aberto && !mensagem) gerar(); }}
        className="w-full flex items-center gap-2.5 px-4 py-3 text-left hover:bg-purple-50 transition-colors">
        <div className="w-7 h-7 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
          <Sparkles size={13} className="text-white"/>
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-purple-800">Mikhal IA — Mensagem de Seleção</p>
          <p className="text-xs text-purple-500">Clique para gerar uma mensagem personalizada</p>
        </div>
        {aberto ? <span className="text-purple-400 text-xs">▲</span> : <span className="text-purple-400 text-xs">▼</span>}
      </button>

      {aberto && (
        <div className="px-4 pb-4 space-y-3 border-t border-purple-100">
          {/* Tipo de mensagem */}
          <div className="flex gap-2 pt-3">
            {[
              { v: 'selecao',    l: '🎯 Seleção' },
              { v: 'entrevista', l: '📅 Entrevista' },
              { v: 'aprovado',   l: '✅ Aprovado' },
            ].map(t => (
              <button key={t.v} onClick={() => { setTipo(t.v); setMensagem(''); }}
                className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-all ${
                  tipo === t.v ? 'bg-purple-600 text-white border-purple-600' : 'border-purple-200 text-purple-600 hover:bg-purple-50'
                }`}>
                {t.l}
              </button>
            ))}
            <button onClick={gerar} disabled={gerando}
              className="ml-auto text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1 disabled:opacity-50">
              {gerando ? <Loader2 size={11} className="animate-spin"/> : <Sparkles size={11}/>}
              {gerando ? 'Gerando...' : 'Gerar nova'}
            </button>
          </div>

          {gerando && !mensagem ? (
            <div className="flex items-center gap-2 py-4 justify-center text-purple-500">
              <Loader2 size={16} className="animate-spin"/>
              <span className="text-sm">Mikhal IA está escrevendo...</span>
            </div>
          ) : mensagem ? (
            <>
              <textarea
                className="w-full text-sm border border-purple-200 rounded-xl p-3 min-h-[100px] resize-none focus:outline-none focus:border-purple-400 bg-white"
                value={mensagem}
                onChange={e => setMensagem(e.target.value)}
                placeholder="Mensagem gerada pela IA..."
              />
              <div className="flex gap-2">
                <button onClick={() => { navigator.clipboard.writeText(mensagem); toast.success('Copiado!'); }}
                  className="flex items-center gap-1.5 text-xs text-gray-500 border border-gray-200 hover:bg-gray-50 px-3 py-2 rounded-xl transition-colors">
                  <Copy size={12}/> Copiar
                </button>
                <button onClick={enviarEAbrir} disabled={enviando}
                  className="flex-1 flex items-center justify-center gap-1.5 text-xs bg-purple-600 hover:bg-purple-700 text-white font-semibold px-3 py-2 rounded-xl transition-colors disabled:opacity-60">
                  {enviando ? <Loader2 size={12} className="animate-spin"/> : <Send size={12}/>}
                  Enviar pelo Chat
                </button>
              </div>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

function CurriculoModal({ candidato, onFechar, onChat, onRegistrar, onAgendar }) {
  const foto = candidato.fotos?.[0] ? `${API_BASE}${candidato.fotos[0]}` : null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-acre-dark to-acre-green px-6 py-5 flex items-center gap-4 rounded-t-2xl">
          <div className="w-16 h-16 rounded-2xl overflow-hidden border-2 border-white/30 flex-shrink-0">
            {foto
              ? <img src={foto} alt={candidato.nome} className="w-full h-full object-cover"/>
              : <div className="w-full h-full bg-white/20 flex items-center justify-center text-white font-bold text-2xl">{candidato.nome?.charAt(0)}</div>}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="font-sora font-bold text-white text-lg">{candidato.nome}</h2>
              {candidato.selo_verificado && <span className="bg-acre-gold text-acre-dark text-xs font-bold px-2 py-0.5 rounded-full">⭐ Ouro</span>}
            </div>
            <p className="text-white/60 text-sm">{candidato.cidade} · {candidato.experiencia_anos||0} anos de experiência</p>
            <div className={`inline-flex items-center gap-1.5 mt-1 text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_LABEL[candidato.status_selecao]?.bg||'bg-gray-100 text-gray-500'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${STATUS_LABEL[candidato.status_selecao]?.dot||'bg-gray-400'}`}/>
              {STATUS_LABEL[candidato.status_selecao]?.l||'Em Seleção'}
            </div>
          </div>
          <button onClick={onFechar} className="text-white/50 hover:text-white text-xl w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10">✕</button>
        </div>

        <div className="p-6 space-y-5">
          {/* Match score */}
          {candidato.score_total && (
            <div className="flex items-center gap-4 bg-gray-50 rounded-xl p-4">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white font-bold font-sora
                ${candidato.score_total>=80?'bg-green-500':candidato.score_total>=60?'bg-lime-500':'bg-yellow-500'}`}>
                {candidato.score_total}%
              </div>
              <div>
                <p className="font-semibold text-acre-dark text-sm">Score de Compatibilidade</p>
                <p className="text-xs text-gray-400">Área: {candidato.score_area}% · Cidade: {candidato.score_geo}%</p>
              </div>
            </div>
          )}

          {/* Resumo */}
          {candidato.resumo_profissional && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase mb-1.5">Resumo Profissional</p>
              <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 rounded-xl p-3">{candidato.resumo_profissional}</p>
            </div>
          )}

          {/* Áreas */}
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase mb-1.5">Áreas de Interesse</p>
            <div className="flex flex-wrap gap-1.5">
              {(candidato.areas_interesse||[]).map(a=><span key={a} className="badge-green">{a}</span>)}
            </div>
          </div>

          {/* Infos */}
          <div className="grid grid-cols-2 gap-3">
            {[
              {l:'Escolaridade',   v:candidato.nivel_escolaridade||'—'},
              {l:'Experiência',    v:`${candidato.experiencia_anos||0} anos`},
              {l:'Disponibilidade',v:(candidato.disponibilidade||'—').replace('_',' ')},
              {l:'Modalidade',     v:candidato.modalidade||'—'},
              {l:'Pretensão',      v:candidato.pretensao_salarial?`R$ ${Number(candidato.pretensao_salarial).toLocaleString('pt-BR')}`:'A combinar'},
            ].map(({l,v})=>(
              <div key={l} className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-400 font-semibold uppercase">{l}</p>
                <p className="text-sm font-semibold text-acre-dark capitalize mt-0.5">{v}</p>
              </div>
            ))}
          </div>

          {/* Experiências */}
          {candidato.experiencias?.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-400 uppercase mb-2">Experiências Anteriores</p>
              <div className="space-y-2">
                {candidato.experiencias.map((e,i)=>(
                  <div key={i} className="flex gap-3 bg-gray-50 rounded-xl p-3">
                    <span className="text-xl mt-0.5">🏢</span>
                    <div>
                      <p className="text-sm font-bold text-acre-dark">{e.cargo}</p>
                      <p className="text-xs text-acre-green font-semibold">{e.empresa}</p>
                      <p className="text-xs text-gray-400">
                        {e.data_inicio&&new Date(e.data_inicio).toLocaleDateString('pt-BR',{month:'short',year:'numeric'})} —{' '}
                        {e.emprego_atual?<span className="text-green-600 font-semibold">Atual</span>:e.data_fim&&new Date(e.data_fim).toLocaleDateString('pt-BR',{month:'short',year:'numeric'})}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Links */}
          <div className="flex flex-wrap gap-2">
            {candidato.video_pitch_url && <a href={candidato.video_pitch_url} target="_blank" rel="noreferrer" className="badge bg-purple-50 text-purple-700 hover:bg-purple-100 py-1.5 px-3">▶ Ver Pitch</a>}
            {candidato.link_portfolio  && <a href={candidato.link_portfolio}  target="_blank" rel="noreferrer" className="badge bg-blue-50 text-blue-700 hover:bg-blue-100 py-1.5 px-3">🔗 Portfólio</a>}
            {candidato.linkedin_url    && <a href={candidato.linkedin_url}    target="_blank" rel="noreferrer" className="badge bg-blue-50 text-blue-700 hover:bg-blue-100 py-1.5 px-3">in LinkedIn</a>}
          </div>

          {/* ── IA Mikhal — gerar mensagem de seleção ── */}
          <MikhalMensagem candidato={candidato} onEnviar={onChat}/>

          {/* Ações */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-gray-100">
            <button onClick={() => onChat(candidato)} className="btn-primary justify-center text-xs py-2.5 flex items-center gap-1.5">
              <MessageCircle size={13}/> Chat
            </button>
            <button onClick={() => onAgendar(candidato)} className="btn-outline justify-center text-xs py-2.5">📅 Agendar</button>
            <button onClick={() => onRegistrar(candidato, 'aprovado')}
              className="bg-green-500 hover:bg-green-600 text-white font-semibold text-xs py-2.5 px-3 rounded-xl transition-all active:scale-95">✅ Aprovado</button>
            <button onClick={() => onRegistrar(candidato, 'recusado')}
              className="bg-red-500 hover:bg-red-600 text-white font-semibold text-xs py-2.5 px-3 rounded-xl transition-all active:scale-95">❌ Recusar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Modal agendar videochamada ──────────────────────────────
function ModalVideochamada({ candidato, onConfirmar, onFechar }) {
  const [data,  setData]  = useState('');
  const [hora,  setHora]  = useState('');
  const [link,  setLink]  = useState('');
  const [obs,   setObs]   = useState('');

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="px-6 py-5 border-b flex items-center justify-between">
          <h3 className="font-sora font-bold text-acre-dark">📅 Agendar Videochamada</h3>
          <button onClick={onFechar} className="text-gray-300 hover:text-gray-500 text-xl">✕</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="bg-blue-50 rounded-xl p-3 text-sm text-blue-700">
            Agendando com <strong>{candidato.nome?.split(' ')[0]}</strong>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Data *</label><input className="input" type="date" value={data} onChange={e=>setData(e.target.value)}/></div>
            <div><label className="label">Hora *</label><input className="input" type="time" value={hora} onChange={e=>setHora(e.target.value)}/></div>
          </div>
          <div><label className="label">Link (Google Meet / Zoom)</label><input className="input" placeholder="https://meet.google.com/..." value={link} onChange={e=>setLink(e.target.value)}/></div>
          <div><label className="label">Observações</label><textarea className="input min-h-16 resize-none" placeholder="Informações adicionais..." value={obs} onChange={e=>setObs(e.target.value)}/></div>
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button onClick={onFechar} className="btn-outline flex-1 justify-center">Cancelar</button>
          <button onClick={() => { if(!data||!hora){toast.error('Preencha data e hora.');return;} onConfirmar({data,hora,link,obs}); }}
            className="btn-primary flex-1 justify-center">📅 Confirmar</button>
        </div>
      </div>
    </div>
  );
}

export default function SelecaoEmpresa() {
  const navigate = useNavigate();
  const [candidatos, setCandidatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [verCurriculo, setVerCurriculo] = useState(null);
  const [agendando, setAgendando] = useState(null);

  useEffect(() => { carregar(); }, [filtroStatus]);

  const carregar = async () => {
    setLoading(true);
    try {
      const q = filtroStatus ? `?status=${filtroStatus}` : '';
      const { data } = await api.get(`/selecao${q}`);
      setCandidatos(data || []);
    } catch { setCandidatos([]); }
    setLoading(false);
  };

  const iniciarChat = async (c) => {
    try {
      await api.post('/chat/conversas', { participante_id: c.user_id });
      navigate('/chat');
    } catch { toast.error('Erro ao abrir chat.'); }
  };

  const registrarResultado = async (c, status) => {
    try {
      await api.put(`/selecao/${c.selecao_id}`, { status });
      toast.success(status==='aprovado' ? '✅ Oportunidade registrada!' : '❌ Recusa registrada.');
      setVerCurriculo(null);
      carregar();
    } catch { toast.error('Erro ao registrar.'); }
  };

  const agendarVideochamada = async (c, dados) => {
    try {
      await api.post(`/selecao/${c.selecao_id}/videochamada`, dados);
      // Envia mensagem automática no chat
      const { data: conv } = await api.post('/chat/conversas', { participante_id: c.user_id });
      await api.post(`/chat/conversas/${conv.id}/mensagens`, {
        conteudo: `📅 Videochamada agendada para ${new Date(dados.data).toLocaleDateString('pt-BR')} às ${dados.hora}${dados.link?`\nLink: ${dados.link}`:''}${dados.obs?`\n${dados.obs}`:''}`
      });
      toast.success('📅 Videochamada agendada e candidato notificado!');
      setAgendando(null);
    } catch { toast.error('Erro ao agendar.'); }
  };

  const totais = {
    em_selecao: candidatos.filter(c=>c.status_selecao==='em_selecao').length,
    entrevista: candidatos.filter(c=>c.status_selecao==='entrevista').length,
    aprovado:   candidatos.filter(c=>c.status_selecao==='aprovado').length,
    recusado:   candidatos.filter(c=>c.status_selecao==='recusado').length,
  };

  return (
    <Layout
      title="📋 Em Seleção"
      subtitle="Candidatos que deram match e estão no processo seletivo"
      action={
        <button onClick={() => navigate('/empresa/vagas')} className="btn-outline text-xs px-4 py-2">← Ver Vagas</button>
      }>

      {/* Resumo */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {Object.entries(totais).map(([k,v])=>(
          <div key={k} className={`card p-3 text-center cursor-pointer transition-all ${filtroStatus===k?'ring-2 ring-acre-green':''}`}
            onClick={()=>setFiltroStatus(filtroStatus===k?'':k)}>
            <p className="font-sora text-2xl font-bold text-acre-dark">{v}</p>
            <p className="text-xs text-gray-400 mt-0.5">{STATUS_LABEL[k]?.l}</p>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-acre-green border-t-transparent rounded-full animate-spin"/></div>
      ) : candidatos.length === 0 ? (
        <div className="card p-10 text-center"><p className="text-4xl mb-2">📋</p><p className="text-gray-500 text-sm">Nenhum candidato em seleção ainda.</p><p className="text-gray-400 text-xs mt-1">Selecione candidatos na página de Match.</p></div>
      ) : (
        <div className="space-y-2.5">
          {candidatos.map(c => {
            const foto = c.fotos?.[0] ? `${API_BASE}${c.fotos[0]}` : null;
            const st = STATUS_LABEL[c.status_selecao] || STATUS_LABEL.em_selecao;
            return (
              <div key={c.selecao_id} className={`card p-4 flex items-center gap-3 hover:shadow-sm transition-all ${
                c.status_selecao === 'em_selecao' ? 'border-l-4 border-l-blue-400 bg-blue-50/20' :
                c.status_selecao === 'aprovado'   ? 'border-l-4 border-l-green-400 bg-green-50/20' : ''
              }`}>
                <div className="w-12 h-12 rounded-xl overflow-hidden border border-gray-100 flex-shrink-0">
                  {foto
                    ? <img src={foto} alt={c.nome} className="w-full h-full object-cover"/>
                    : <div className="w-full h-full bg-gradient-to-br from-acre-green to-acre-teal flex items-center justify-center text-white font-bold text-lg">{c.nome?.charAt(0)}</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-sora font-semibold text-sm text-acre-dark">{c.nome}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 ${st.bg}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}/>{st.l}
                    </span>
                    {c.score_total && <span className="badge-green text-xs">{c.score_total}% match</span>}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{c.cidade} · {c.vaga_titulo}</p>
                  {c.videochamada_data && (
                    <p className="text-xs text-yellow-600 font-semibold mt-0.5">
                      📅 Entrevista: {new Date(c.videochamada_data).toLocaleDateString('pt-BR')} às {c.videochamada_hora}
                    </p>
                  )}
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <button onClick={() => setVerCurriculo(c)} className="btn-primary text-xs px-3 py-2">📄 Currículo</button>
                  <button onClick={() => iniciarChat(c)} className="btn-outline text-xs px-3 py-2">💬</button>
                  <button onClick={() => setAgendando(c)} className="text-xs px-3 py-2 border border-yellow-300 text-yellow-600 rounded-xl hover:bg-yellow-50 transition-all">📅</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {verCurriculo && (
        <CurriculoModal
          candidato={verCurriculo}
          onFechar={() => setVerCurriculo(null)}
          onChat={iniciarChat}
          onRegistrar={(c, status) => registrarResultado(c, status)}
          onAgendar={(c) => { setVerCurriculo(null); setAgendando(c); }}
        />
      )}

      {agendando && (
        <ModalVideochamada
          candidato={agendando}
          onConfirmar={(dados) => agendarVideochamada(agendando, dados)}
          onFechar={() => setAgendando(null)}
        />
      )}
    </Layout>
  );
}
